import { createContext, useContext, useState, useEffect, useMemo } from "react";

const CartContext = createContext();

export function CartProvider({ children }) {
  // Charger le panier depuis localStorage au démarrage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem("fleuriste_cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });

  // Sauvegarder le panier dans localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem("fleuriste_cart", JSON.stringify(cartItems));
  }, [cartItems]);

  // Ajouter un produit au panier
  const addToCart = (product, variant, quantity = 1, bouquetType = null) => {
    setCartItems((prev) => {
      // Créer un identifiant unique pour cette combinaison produit/variante/type
      const itemId = `${product.id}-${variant.taille_code}${bouquetType ? `-${bouquetType}` : ""}`;
      
      const existingIndex = prev.findIndex((item) => item.itemId === itemId);
      
      if (existingIndex > -1) {
        // Mettre à jour la quantité si l'item existe déjà
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: Math.min(
            updated[existingIndex].quantity + quantity,
            variant.stock
          ),
        };
        return updated;
      }
      
      // Ajouter un nouvel item
      return [
        ...prev,
        {
          itemId,
          product,
          variant,
          quantity,
          bouquetType,
          selected: true, // Par défaut, les items sont sélectionnés
          addedAt: new Date().toISOString(),
        },
      ];
    });
  };

  // Retirer un produit du panier
  const removeFromCart = (itemId) => {
    setCartItems((prev) => prev.filter((item) => item.itemId !== itemId));
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = (itemId, quantity) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.itemId === itemId
          ? { ...item, quantity: Math.max(1, Math.min(quantity, item.variant.stock)) }
          : item
      )
    );
  };

  // Mettre à jour la configuration d'un produit (taille, type de bouquet)
  const updateItemConfig = (itemId, newVariant, newBouquetType = null) => {
    setCartItems((prev) => {
      const itemIndex = prev.findIndex((item) => item.itemId === itemId);
      if (itemIndex === -1) return prev;

      const item = prev[itemIndex];
      const product = item.product;
      
      // Créer le nouvel identifiant
      const newItemId = `${product.id}-${newVariant.taille_code}${newBouquetType ? `-${newBouquetType}` : ""}`;
      
      // Vérifier si un item avec cette config existe déjà
      const existingIndex = prev.findIndex((i) => i.itemId === newItemId && i.itemId !== itemId);
      
      if (existingIndex > -1) {
        // Fusionner avec l'item existant
        const updated = [...prev];
        const newQuantity = Math.min(
          updated[existingIndex].quantity + item.quantity,
          newVariant.stock
        );
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: newQuantity,
        };
        // Supprimer l'ancien item
        return updated.filter((_, index) => index !== itemIndex);
      }
      
      // Mettre à jour l'item avec la nouvelle config
      const updated = [...prev];
      updated[itemIndex] = {
        ...item,
        itemId: newItemId,
        variant: newVariant,
        bouquetType: newBouquetType,
        quantity: Math.min(item.quantity, newVariant.stock), // Ajuster si stock insuffisant
      };
      return updated;
    });
  };

  // Basculer la sélection d'un item
  const toggleSelection = (itemId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.itemId === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  // Sélectionner/Désélectionner tous les items
  const selectAll = (selected) => {
    setCartItems((prev) =>
      prev.map((item) => ({ ...item, selected }))
    );
  };

  // Supprimer les items sélectionnés
  const removeSelected = () => {
    setCartItems((prev) => prev.filter((item) => !item.selected));
  };

  // Vider le panier
  const clearCart = () => {
    setCartItems([]);
  };

  // Calculer le prix TTC
  const calculateTTC = (prixHT, tva) => {
    return prixHT * (1 + tva / 100);
  };

  // Calculer les totaux
  const totals = useMemo(() => {
    const selectedItems = cartItems.filter((item) => item.selected);
    const allItems = cartItems;

    const calculateSubtotal = (items) =>
      items.reduce((sum, item) => {
        const priceTTC = calculateTTC(item.variant.prixHT, item.product.tva);
        return sum + priceTTC * item.quantity;
      }, 0);

    return {
      itemCount: allItems.length,
      selectedCount: selectedItems.length,
      subtotalAll: calculateSubtotal(allItems),
      subtotalSelected: calculateSubtotal(selectedItems),
      totalQuantity: allItems.reduce((sum, item) => sum + item.quantity, 0),
      selectedQuantity: selectedItems.reduce((sum, item) => sum + item.quantity, 0),
    };
  }, [cartItems]);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateItemConfig,
    toggleSelection,
    selectAll,
    removeSelected,
    clearCart,
    totals,
    calculateTTC,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart doit être utilisé dans un CartProvider");
  }
  return context;
}

export default CartContext;
